/**
 * Curated SVG library for quest + milestone icons.
 *
 * The backend's Quest schema includes an ``iconKey`` field whose
 * value comes from a curated vocabulary (see
 * ``QUEST_ICON_KEYS`` in ``quest_recommendation.py``).  The
 * frontend resolves the key to inline SVG markup via this module.
 *
 * Unknown keys fall back to the ``generic`` icon so the UI never
 * crashes on an off-vocabulary key.
 *
 * Icons are loaded via Vite's ``import.meta.glob`` with ``?raw``
 * so the SVG markup string is bundled at build time — no extra
 * network requests, and the markup can be rendered inline via
 * ``dangerouslySetInnerHTML`` so the SVG inherits ``currentColor``
 * from the surrounding text style.
 */

const _rawSvgs = import.meta.glob<string>(
  "./quest_icons/*.svg",
  { eager: true, query: "?raw", import: "default" }
);

const _iconByKey: Record<string, string> = {};
for (const [path, svg] of Object.entries(_rawSvgs)) {
  // path looks like "./quest_icons/variance.svg" — strip prefix + suffix
  const m = path.match(/\/([^/]+)\.svg$/);
  if (m) _iconByKey[m[1]] = svg;
}

const FALLBACK_KEY = "generic";

/** Return the SVG markup for a quest/milestone icon key.  Falls
 * back to the generic icon if the key isn't in the curated set. */
export function questIconSvg(iconKey: string | undefined | null): string {
  if (iconKey && _iconByKey[iconKey]) return _iconByKey[iconKey];
  return _iconByKey[FALLBACK_KEY] ?? "";
}
